// src/components/demo/DemoBoardScreen.js
//
// Board card after a scan (SPEC §4 / §8.3 screen 4). The branch point:
//   AVAILABLE    → CHECK OUT
//   OUT          → CHECK IN
//   NOT IN FLEET → explicit add-to-fleet confirm (typo guard)
//   MAINTENANCE  → clear-maintenance (returns board to AVAILABLE)

import React, { useState } from 'react';
import { FScreen, FHeader, FHeadline, FBtn, FLabel, FBoardCard } from '../foam/FoamPrimitives';

function elapsedLabel(checkedOutAt) {
  const t = checkedOutAt?.toDate ? checkedOutAt.toDate() : null;
  if (!t) return '';
  const mins = Math.max(0, Math.round((Date.now() - t.getTime()) / 60000));
  return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
}

export default function DemoBoardScreen({
  product, fleetBoard, openSession, registeredOwner, onCheckOut, onCheckIn, onAddToFleet, onClearMaintenance, onBack,
}) {
  const [busy, setBusy] = useState(false);
  const status = fleetBoard?.status || 'not-in-fleet';

  let statusLabel; let footer;
  if (status === 'available') {
    statusLabel = <FLabel color="var(--foam-ok)">Available</FLabel>;
    // Rider-types-own flow (Nigel 2026-07-22): tapping CHECK OUT is the moment
    // the device is handed to the rider — they enter their own details + sign.
    footer = <FBtn primary onClick={onCheckOut}>Check out — hand to rider</FBtn>;
  } else if (status === 'out') {
    const rider = openSession ? `${openSession.riderFirstName} ${openSession.riderLastName}` : '…';
    statusLabel = (
      <FLabel color="var(--foam-orange)">
        Out · {rider} · {openSession ? elapsedLabel(openSession.checkedOutAt) : ''}
      </FLabel>
    );
    footer = <FBtn primary onClick={onCheckIn}>Check in</FBtn>;
  } else if (status === 'maintenance') {
    statusLabel = <FLabel color="var(--foam-orange)">Maintenance · {fleetBoard.maintenanceNote || ''}</FLabel>;
    footer = (
      <FBtn
        onClick={async () => { setBusy(true); try { await onClearMaintenance(); } finally { setBusy(false); } }}
        disabled={busy}
      >
        Back in service
      </FBtn>
    );
  } else if (registeredOwner) {
    // Personal-board guard (Nigel 2026-07-22): this serial is registered to a
    // real owner. Never absorb it silently — explicit temporary-loaner confirm
    // only, and ownership is untouched either way.
    // Design r2 (DBoardLoaner): teal/lagoon = "on file" semantic — visually
    // distinct from both the orange check-out path and the plain add-to-fleet.
    statusLabel = (
      <span
        className="font-mono font-bold uppercase"
        style={{ fontSize: 10, letterSpacing: '0.08em', padding: '5px 9px',
          background: 'var(--foam-lagoon)', color: 'var(--foam-teal)', whiteSpace: 'nowrap' }}
      >
        ● On file · {registeredOwner.ownerName}'s
      </span>
    );
    footer = (
      <FBtn
        primary
        style={{ background: 'var(--foam-teal)', color: 'var(--foam-paper)' }}
        disabled={busy}
        onClick={async () => { setBusy(true); try { await onAddToFleet(); } finally { setBusy(false); } }}
      >
        Add as loaner — {registeredOwner.ownerName}'s board
      </FBtn>
    );
  } else {
    statusLabel = <FLabel>Not in today's fleet</FLabel>;
    footer = (
      <FBtn
        primary
        disabled={busy}
        onClick={async () => { setBusy(true); try { await onAddToFleet(); } finally { setBusy(false); } }}
      >
        Add to today's fleet
      </FBtn>
    );
  }

  return (
    <FScreen
      header={<FHeader right={statusLabel} />}
      footer={
        <>
          {footer}
          <FBtn ghost onClick={onBack}>
            {status === 'not-in-fleet' && registeredOwner ? 'Not this board' : 'Back'}
          </FBtn>
        </>
      }
    >
      <FBoardCard product={product} />
      <FHeadline size={44}>
        {status === 'available' && <>Ready to<br /><FHeadline.Accent>rip.</FHeadline.Accent></>}
        {status === 'out' && <>Coming<br /><FHeadline.Accent>home?</FHeadline.Accent></>}
        {status === 'maintenance' && <>Benched<br /><FHeadline.Accent>for now.</FHeadline.Accent></>}
        {status === 'not-in-fleet' && !registeredOwner && <>New board<br /><FHeadline.Accent>on the rack?</FHeadline.Accent></>}
        {status === 'not-in-fleet' && registeredOwner && <>{registeredOwner.ownerName}'s<br /><FHeadline.Accent>own board.</FHeadline.Accent></>}
      </FHeadline>
      {status === 'not-in-fleet' && !registeredOwner && (
        <p className="font-sans text-[14px]" style={{ color: 'var(--foam-ink-2)' }}>
          Double-check the serial above matches the board in your hands before adding it.
        </p>
      )}
      {status === 'not-in-fleet' && registeredOwner && (
        <div
          className="font-sans text-[14px]"
          style={{ color: 'var(--foam-ink)', lineHeight: 1.55, padding: 16,
            border: '1.5px solid var(--foam-teal)', background: 'var(--foam-lagoon)' }}
        >
          This board is registered to <strong>{registeredOwner.ownerName}</strong>. Adding it as a
          loaner lets riders demo it today — <strong>ownership doesn't change</strong>, and it drops
          off the fleet when the day ends. It stays {registeredOwner.ownerName}'s board on my.waka.surf.
        </div>
      )}
    </FScreen>
  );
}
